import {
    useEffect,
    useRef,
    useCallback,
    useState,
    useMemo,
    memo,
    createContext,
    useContext,
} from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Icon } from '@iconify/react'
import { Typography, Modal, Row, Tooltip } from 'antd'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Thumbs, Zoom } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/thumbs'
import 'swiper/css/zoom'

import {
    fetchTaskAttachmentsList,
    projectsState,
    resetTaskAttachments,
    deleteTaskAttachmentAction,
} from '../projects.slice'
import {
    FILES_TAB_BAR_HOST_ATTR,
    FILES_TAB_BAR_SLOT_ATTR,
    attachTabBarSlot,
    removeTabBarHost,
} from './projectTabBarPortal'

import styles from './ProjectFiles.module.css'

const { Text } = Typography

const FilesScrollRootContext = createContext(null)

// ─── helpers ────────────────────────────────────────────────────────────────

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'mkv', 'webm', 'wmv']
const SHEET_EXTENSIONS = ['xls', 'xlsx', 'csv', 'ods']

const getExtension = (url = '') => {
    try {
        const clean = decodeURIComponent(url.split('?')[0] || '')
        return clean.split('.').pop()?.toLowerCase() || ''
    } catch {
        return url.split('?')[0].split('.').pop()?.toLowerCase() || ''
    }
}

const getFileKind = (url) => {
    const ext = getExtension(url)
    if (IMAGE_EXTENSIONS.includes(ext)) return 'image'
    if (VIDEO_EXTENSIONS.includes(ext)) return 'video'
    if (SHEET_EXTENSIONS.includes(ext)) return 'sheet'
    if (ext === 'pdf') return 'pdf'
    if (['doc', 'docx'].includes(ext)) return 'doc'
    if (['ppt', 'pptx'].includes(ext)) return 'ppt'
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive'
    return 'other'
}

const getFileIcon = (kind) => {
    switch (kind) {
        case 'image':
            return 'proicons:photo'
        case 'video':
            return 'ep:video-play'
        case 'sheet':
            return 'tabler:table'
        case 'pdf':
            return 'formkit:filepdf'
        case 'doc':
            return 'f7:doc'
        case 'ppt':
            return 'hugeicons:ppt-02'
        case 'archive':
            return 'lsicon:file-zip-filled'
        default:
            return 'tabler:file'
    }
}

const getKindLabel = (kind) => {
    switch (kind) {
        case 'image':
            return 'Image'
        case 'video':
            return 'Video'
        case 'sheet':
            return 'Spreadsheet'
        case 'pdf':
            return 'PDF'
        case 'doc':
            return 'Document'
        case 'ppt':
            return 'Presentation'
        case 'archive':
            return 'Archive'
        default:
            return 'File'
    }
}

const getFileName = (url = '') => {
    try {
        const clean = decodeURIComponent(url.split('?')[0] || '')
        return clean.split('/').pop() || 'Untitled File'
    } catch {
        return url.split('/').pop() || 'Untitled File'
    }
}

const getDisplayName = (url = '') => {
    const full = getFileName(url)
    const ext = getExtension(url)
    const stem = ext ? full.slice(0, -(ext.length + 1)) : full
    const cleaned = stem
        .replace(/(_\d{10,}){1,3}$/g, '')
        .replace(/[-_]+$/g, '')
        .trim()
    const label = cleaned || stem || 'Untitled File'
    return ext ? `${label}.${ext}` : label
}

const VIEW_STORAGE_KEY = 'pmx.projectFiles.viewMode'

const getStoredViewMode = () => {
    try {
        const stored = localStorage.getItem(VIEW_STORAGE_KEY)
        return stored === 'grid' || stored === 'list' ? stored : 'list'
    } catch {
        return 'list'
    }
}

// ─── sub-components ─────────────────────────────────────────────────────────

const FileActions = memo(function FileActions({
    url,
    attachment,
    onDownload,
    onDelete,
}) {
    return (
        <div className={styles.rowActions}>
            <button
                type="button"
                className={styles.actionBtn}
                aria-label="Download"
                title="Download"
                onClick={(e) => {
                    e.stopPropagation()
                    onDownload(url)
                }}
            >
                <Icon icon="akar-icons:download" width={16} />
            </button>
            <button
                type="button"
                className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                aria-label="Delete"
                title="Delete"
                onClick={(e) => {
                    e.stopPropagation()
                    onDelete?.(attachment)
                }}
            >
                <Icon icon="solar:trash-bin-trash-linear" width={16} />
            </button>
        </div>
    )
})

const FileMeta = memo(function FileMeta({
    taskTitle,
    taskId,
    author,
    onTaskClick,
    className = styles.meta,
}) {
    return (
        <div className={className}>
            {taskTitle ? (
                <button
                    type="button"
                    className={styles.taskLink}
                    onClick={(e) => {
                        e.stopPropagation()
                        taskId && onTaskClick?.(taskId)
                    }}
                >
                    {taskTitle}
                </button>
            ) : (
                <span className={styles.metaMuted}>No task</span>
            )}
            {author ? (
                <>
                    <span className={styles.dot}>·</span>
                    <span className={styles.author}>{author}</span>
                </>
            ) : null}
        </div>
    )
})

/** Load image only when near the files scrollport. */
const LazyThumbImage = memo(function LazyThumbImage({
    url,
    fileName,
    isLarge,
}) {
    const scrollRoot = useContext(FilesScrollRootContext)
    const wrapRef = useRef(null)
    const [shouldLoad, setShouldLoad] = useState(false)
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (shouldLoad) return undefined
        const node = wrapRef.current
        if (!node) return undefined

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setShouldLoad(true)
                    observer.disconnect()
                }
            },
            {
                root: scrollRoot || null,
                rootMargin: '120px 0px',
                threshold: 0.01,
            }
        )
        observer.observe(node)
        return () => observer.disconnect()
    }, [scrollRoot, shouldLoad])

    if (failed) {
        return (
            <div
                className={`${styles.thumb} ${isLarge ? styles.thumbLarge : ''} ${styles.thumb_image}`}
            >
                <Icon icon="proicons:photo" width={isLarge ? 32 : 20} />
                {isLarge ? (
                    <span className={styles.thumbKindLabel}>Image</span>
                ) : null}
            </div>
        )
    }

    return (
        <div
            ref={wrapRef}
            className={`${styles.thumb} ${isLarge ? styles.thumbLarge : ''} ${styles.thumb_image}`}
        >
            {shouldLoad ? (
                <img
                    src={url}
                    alt={fileName}
                    className={styles.thumbImg}
                    decoding="async"
                    fetchPriority="low"
                    onError={() => setFailed(true)}
                />
            ) : (
                <div className={styles.thumbPlaceholder} />
            )}
        </div>
    )
})

const FileThumb = memo(function FileThumb({
    url,
    kind,
    fileName,
    size = 'sm',
    showImage = true,
}) {
    const icon = getFileIcon(kind)
    const isLarge = size === 'lg'

    if (kind === 'image' && showImage) {
        return (
            <LazyThumbImage url={url} fileName={fileName} isLarge={isLarge} />
        )
    }

    return (
        <div
            className={`${styles.thumb} ${isLarge ? styles.thumbLarge : ''} ${styles[`thumb_${kind}`]}`}
        >
            <Icon icon={icon} width={isLarge ? 32 : 20} />
            {isLarge ? (
                <span className={styles.thumbKindLabel}>
                    {getKindLabel(kind)}
                </span>
            ) : null}
        </div>
    )
})

const FileRow = memo(function FileRow({
    attachment,
    index,
    onOpen,
    onDownload,
    onTaskClick,
    onDelete,
}) {
    const url = attachment?.attachment || ''
    const fileName = getFileName(url)
    const displayName = getDisplayName(url)
    const taskTitle = attachment?.taskTitle || ''
    const taskId = attachment?.taskId
    const author = attachment?.commentUsername
    const kind = getFileKind(url)
    const ext = getExtension(url).toUpperCase()

    return (
        <div
            className={styles.row}
            onClick={() => onOpen(index)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen(index)
                }
            }}
        >
            {/* Icons only in list view — avoids decoding full images while scrolling */}
            <FileThumb
                url={url}
                kind={kind}
                fileName={displayName}
                showImage={false}
            />

            <div className={styles.rowBody}>
                <Text className={styles.fileName} ellipsis title={fileName}>
                    {displayName}
                </Text>
                <FileMeta
                    taskTitle={taskTitle}
                    taskId={taskId}
                    author={author}
                    onTaskClick={onTaskClick}
                />
            </div>

            <span className={styles.typeChip}>
                {ext || getKindLabel(kind)}
            </span>

            <FileActions
                url={url}
                attachment={attachment}
                onDownload={onDownload}
                onDelete={onDelete}
            />
        </div>
    )
})

const FileCard = memo(function FileCard({
    attachment,
    index,
    onOpen,
    onDownload,
    onTaskClick,
    onDelete,
}) {
    const url = attachment?.attachment || ''
    const fileName = getFileName(url)
    const displayName = getDisplayName(url)
    const taskTitle = attachment?.taskTitle || ''
    const taskId = attachment?.taskId
    const author = attachment?.commentUsername
    const kind = getFileKind(url)
    const ext = getExtension(url).toUpperCase()

    return (
        <div
            className={styles.card}
            onClick={() => onOpen(index)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onOpen(index)
                }
            }}
        >
            <div className={styles.cardPreview}>
                <FileThumb
                    url={url}
                    kind={kind}
                    fileName={displayName}
                    size="lg"
                    showImage
                />
            </div>
            <div className={styles.cardBody}>
                <Text className={styles.fileName} ellipsis title={fileName}>
                    {displayName}
                </Text>
                <FileMeta
                    taskTitle={taskTitle}
                    taskId={taskId}
                    author={author}
                    onTaskClick={onTaskClick}
                    className={styles.cardMeta}
                />
                <div className={styles.cardFooter}>
                    <span className={styles.cardType}>
                        {ext || getKindLabel(kind)}
                    </span>
                    <FileActions
                        url={url}
                        attachment={attachment}
                        onDownload={onDownload}
                        onDelete={onDelete}
                    />
                </div>
            </div>
        </div>
    )
})

// ─── main component ──────────────────────────────────────────────────────────

const LIMIT = 12

const ProjectFiles = ({ projectId, onTaskClick }) => {
    const dispatch = useDispatch()

    const {
        taskAttachmentsList,
        taskAttachmentsIsNext,
        taskAttachmentsStatus,
        taskAttachmentsPage,
    } = useSelector(projectsState)

    const [isMediaOpen, setIsMediaOpen] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)
    const [viewMode, setViewMode] = useState(getStoredViewMode)
    const [scrollRootEl, setScrollRootEl] = useState(null)
    const [tabBarSlot, setTabBarSlot] = useState(null)
    const [useInlineToolbar, setUseInlineToolbar] = useState(false)
    const swiperRef = useRef(null)
    const prevRef = useRef(null)
    const nextRef = useRef(null)

    const sentinelRef = useRef(null)
    const scrollContainerRef = useRef(null)
    const isFetchingRef = useRef(false)
    const requestIdRef = useRef(0)
    const hasUserScrolledRef = useRef(false)
    const scrollEndTimerRef = useRef(null)

    const setScrollContainerNode = useCallback((node) => {
        scrollContainerRef.current = node
        setScrollRootEl(node)
    }, [])

    const handleViewModeChange = (mode) => {
        setViewMode(mode)
        try {
            localStorage.setItem(VIEW_STORAGE_KEY, mode)
        } catch {
            // ignore storage errors
        }
    }

    useEffect(() => {
        if (isMediaOpen && swiperRef.current) {
            swiperRef.current.slideTo(activeIndex)
        }
    }, [isMediaOpen, activeIndex])

    useEffect(() => {
        let timer = null
        let retryTimer = null

        const attachToolbar = () => {
            const slot = attachTabBarSlot({
                hostAttr: FILES_TAB_BAR_HOST_ATTR,
                slotAttr: FILES_TAB_BAR_SLOT_ATTR,
                hostClassName: styles.tabBarHost,
            })
            if (!slot) return false
            setTabBarSlot(slot)
            setUseInlineToolbar(false)
            return true
        }

        if (!attachToolbar()) {
            timer = window.setTimeout(attachToolbar, 0)
            retryTimer = window.setTimeout(() => {
                if (!attachToolbar()) setUseInlineToolbar(true)
            }, 120)
        }

        return () => {
            if (timer) window.clearTimeout(timer)
            if (retryTimer) window.clearTimeout(retryTimer)
            removeTabBarHost(FILES_TAB_BAR_HOST_ATTR)
            setTabBarSlot(null)
            setUseInlineToolbar(false)
        }
    }, [projectId])

    const handleFileClick = useCallback((index) => {
        setActiveIndex(index)
        setIsMediaOpen(true)
    }, [])

    const handleDownload = useCallback(async (url) => {
        try {
            const response = await fetch(url, { mode: 'cors' })
            if (!response.ok) throw new Error('Network response was not ok')
            const blob = await response.blob()
            const blobUrl = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = getFileName(url) || 'download'
            document.body.appendChild(a)
            a.click()
            a.remove()
            window.URL.revokeObjectURL(blobUrl)
        } catch (err) {
            window.open(url, '_blank')
        }
    }, [])

    const preparedFiles = useMemo(
        () =>
            taskAttachmentsList?.map((item) => {
                const url = item?.attachment || ''
                const ext = getExtension(url)
                let type = 'other'
                if (IMAGE_EXTENSIONS.includes(ext)) type = 'image'
                else if (VIDEO_EXTENSIONS.includes(ext)) type = 'video'
                else if (ext === 'pdf') type = 'pdf'
                else if (['ppt', 'pptx'].includes(ext)) type = 'pptx'

                return { src: url, type }
            }),
        [taskAttachmentsList]
    )

    useEffect(() => {
        if (!projectId) return

        const requestId = ++requestIdRef.current
        isFetchingRef.current = true
        hasUserScrolledRef.current = false
        dispatch(resetTaskAttachments())
        dispatch(
            fetchTaskAttachmentsList({ projectId, skip: 0, limit: LIMIT })
        ).finally(() => {
            if (requestIdRef.current === requestId) {
                isFetchingRef.current = false
            }
        })

        return () => {
            requestIdRef.current += 1
            isFetchingRef.current = false
            hasUserScrolledRef.current = false
            dispatch(resetTaskAttachments())
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId])

    const loadMore = useCallback(() => {
        if (
            !hasUserScrolledRef.current ||
            isFetchingRef.current ||
            !taskAttachmentsIsNext ||
            taskAttachmentsStatus === 'loading'
        )
            return

        const requestId = ++requestIdRef.current
        isFetchingRef.current = true
        const skip = taskAttachmentsPage * LIMIT

        dispatch(
            fetchTaskAttachmentsList({ projectId, skip, limit: LIMIT })
        ).finally(() => {
            if (requestIdRef.current === requestId) {
                isFetchingRef.current = false
            }
        })
    }, [
        dispatch,
        projectId,
        taskAttachmentsIsNext,
        taskAttachmentsPage,
        taskAttachmentsStatus,
    ])

    const handleScroll = useCallback(() => {
        const root = scrollContainerRef.current
        if (!root) return

        // Only unlock pagination after the user actually scrolls
        if (root.scrollTop > 8) {
            hasUserScrolledRef.current = true
        }

        if (scrollEndTimerRef.current) {
            clearTimeout(scrollEndTimerRef.current)
        }
        scrollEndTimerRef.current = setTimeout(() => {
            if (!hasUserScrolledRef.current) return
            const sentinel = sentinelRef.current
            if (!sentinel) return
            const rootRect = root.getBoundingClientRect()
            const sentinelRect = sentinel.getBoundingClientRect()
            if (sentinelRect.top <= rootRect.bottom + 16) {
                loadMore()
            }
        }, 100)
    }, [loadMore])

    useEffect(() => {
        return () => {
            if (scrollEndTimerRef.current) {
                clearTimeout(scrollEndTimerRef.current)
            }
        }
    }, [])

    const handleDelete = useCallback(
        (attachment) => {
            if (!attachment) return
            dispatch(
                deleteTaskAttachmentAction({
                    taskId: attachment.taskId,
                    type: attachment.type || 'taskAttachment',
                    attachment: attachment.attachment,
                })
            )
        },
        [dispatch]
    )

    useEffect(() => {
        const el = sentinelRef.current
        const root = scrollContainerRef.current
        if (!el || !root) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0].isIntersecting) return
                if (!hasUserScrolledRef.current) return
                loadMore()
            },
            { root, rootMargin: '0px', threshold: 0.1 }
        )

        observer.observe(el)
        return () => observer.disconnect()
    }, [loadMore, taskAttachmentsList?.length])

    const isInitialLoading =
        taskAttachmentsStatus === 'loading' && taskAttachmentsList?.length === 0

    if (isInitialLoading) {
        const loadingToolbar = (
            <div className={styles.tabBarToolbar}>
                <div className={styles.viewToggle} role="group" aria-label="View mode">
                    <button
                        type="button"
                        className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnActive : ''}`}
                        aria-label="List view"
                        onClick={() => handleViewModeChange('list')}
                    >
                        <Icon icon="hugeicons:left-to-right-list-dash" width={18} />
                    </button>
                    <button
                        type="button"
                        className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
                        aria-label="Grid view"
                        onClick={() => handleViewModeChange('grid')}
                    >
                        <Icon icon="hugeicons:grid-view" width={18} />
                    </button>
                </div>
            </div>
        )

        return (
            <>
                {tabBarSlot ? createPortal(loadingToolbar, tabBarSlot) : null}
                <div className={styles.container} ref={setScrollContainerNode}>
                    {useInlineToolbar ? (
                        <div className={styles.inlineToolbar}>
                            {loadingToolbar}
                        </div>
                    ) : null}
                    <div className={styles.skeletonList}>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div key={i} className={styles.skeletonRow}>
                                <div className={styles.skeletonThumb} />
                                <div className={styles.skeletonBody}>
                                    <div className={styles.skeletonLine} />
                                    <div
                                        className={`${styles.skeletonLine} ${styles.skeletonLineShort}`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </>
        )
    }

    if (
        taskAttachmentsStatus !== 'loading' &&
        taskAttachmentsList?.length === 0
    ) {
        return (
            <div className={styles.centered}>
                <div className={styles.emptyIconWrap}>
                    <Icon icon="tabler:files-off" width={28} color="#94a3b8" />
                </div>
                <Text className={styles.emptyText}>No files attached yet</Text>
                <Text className={styles.emptySubText}>
                    Files added to tasks will appear here
                </Text>
            </div>
        )
    }

    const FileItem = viewMode === 'grid' ? FileCard : FileRow

    const filesToolbar = (
        <div className={styles.tabBarToolbar}>
            <div
                className={styles.viewToggle}
                role="group"
                aria-label="View mode"
            >
                <Tooltip title="List view">
                    <button
                        type="button"
                        className={`${styles.viewBtn} ${viewMode === 'list' ? styles.viewBtnActive : ''}`}
                        aria-label="List view"
                        aria-pressed={viewMode === 'list'}
                        onClick={() => handleViewModeChange('list')}
                    >
                        <Icon icon="hugeicons:left-to-right-list-dash" width={18} />
                    </button>
                </Tooltip>
                <Tooltip title="Grid view">
                    <button
                        type="button"
                        className={`${styles.viewBtn} ${viewMode === 'grid' ? styles.viewBtnActive : ''}`}
                        aria-label="Grid view"
                        aria-pressed={viewMode === 'grid'}
                        onClick={() => handleViewModeChange('grid')}
                    >
                        <Icon icon="hugeicons:grid-view" width={18} />
                    </button>
                </Tooltip>
            </div>
        </div>
    )

    return (
        <FilesScrollRootContext.Provider value={scrollRootEl}>
            {tabBarSlot ? createPortal(filesToolbar, tabBarSlot) : null}
            <div
                className={styles.container}
                ref={setScrollContainerNode}
                onScroll={handleScroll}
            >
                {useInlineToolbar ? (
                    <div className={styles.inlineToolbar}>{filesToolbar}</div>
                ) : null}

                <div
                    className={
                        viewMode === 'grid' ? styles.grid : styles.list
                    }
                >
                    {taskAttachmentsList.map((attachment, index) => (
                        <FileItem
                            key={
                                attachment?._id ||
                                attachment?.attachment ||
                                index
                            }
                            attachment={attachment}
                            index={index}
                            onOpen={handleFileClick}
                            onDownload={handleDownload}
                            onTaskClick={onTaskClick}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>

                <div ref={sentinelRef} className={styles.sentinel} />

                {taskAttachmentsStatus === 'loading' &&
                    taskAttachmentsList?.length > 0 && (
                        <div className={styles.loadingMore}>
                            <Icon
                                icon="line-md:loading-loop"
                                style={{ fontSize: 22, color: '#6366f1' }}
                            />
                        </div>
                    )}

                <Modal
                    open={isMediaOpen}
                    onCancel={(e) => {
                        e.stopPropagation()
                        setIsMediaOpen(false)
                    }}
                    footer={null}
                    closeIcon={null}
                    centered
                    width="100vw"
                    style={{
                        top: 0,
                        padding: 0,
                        maxWidth: '100vw',
                    }}
                    styles={{
                        header: {
                            backgroundColor: '#252628',
                            padding: '10px 24px',
                            borderRadius: 0,
                            borderBottom: '1px solid #4b4b4b',
                        },
                        body: {
                            padding: 0,
                            height: 'calc(100vh - 55px)',
                            backgroundColor: '#252628',
                        },
                        content: {
                            padding: 0,
                            margin: 0,
                            height: '100vh',
                            backgroundColor: '#252628',
                            borderRadius: 0,
                        },
                    }}
                    title={
                        <Row align={'middle'} justify="space-between">
                            <Row>
                                <Text
                                    style={{ color: '#fff', fontWeight: 200 }}
                                >
                                    {getFileName(
                                        taskAttachmentsList[activeIndex]
                                            ?.attachment
                                    )}
                                </Text>
                            </Row>
                            <Row
                                justify="end"
                                align="middle"
                                style={{ columnGap: '15px' }}
                            >
                                <Row
                                    align="middle"
                                    onClick={() =>
                                        handleDownload(
                                            taskAttachmentsList[activeIndex]
                                                ?.attachment
                                        )
                                    }
                                    style={{
                                        cursor: 'pointer',
                                        color: '#fff',
                                        columnGap: 6,
                                    }}
                                >
                                    <Icon
                                        icon="akar-icons:download"
                                        height={18}
                                        width={18}
                                    />
                                    <Text
                                        style={{
                                            color: '#fff',
                                            fontWeight: 400,
                                        }}
                                    >
                                        Download
                                    </Text>
                                </Row>
                                <Tooltip title="Close" placement="top">
                                    <Icon
                                        icon="akar-icons:cross"
                                        onClick={() => setIsMediaOpen(false)}
                                        height={18}
                                        width={18}
                                        style={{
                                            cursor: 'pointer',
                                            color: '#fff',
                                        }}
                                    />
                                </Tooltip>
                            </Row>
                        </Row>
                    }
                >
                    <div className="relative">
                        <div className="custom-swiper-buttons">
                            <div
                                ref={prevRef}
                                className="swiper-button-prev"
                                style={{ cursor: 'pointer' }}
                            >
                                <Icon icon="ph:arrow-left-bold" />
                            </div>
                            <div
                                ref={nextRef}
                                className="swiper-button-next"
                                style={{ cursor: 'pointer' }}
                            >
                                <Icon icon="ph:arrow-right-bold" />
                            </div>
                        </div>

                        <Swiper
                            style={{ height: '85vh' }}
                            modules={[Navigation, Thumbs, Zoom]}
                            navigation={{
                                nextEl: '.swiper-button-next',
                                prevEl: '.swiper-button-prev',
                            }}
                            zoom={{ maxRatio: 3 }}
                            onSwiper={(swiper) => {
                                swiperRef.current = swiper
                                setTimeout(() => {
                                    if (
                                        prevRef.current &&
                                        nextRef.current &&
                                        swiperRef.current
                                    ) {
                                        swiperRef.current.params.navigation.prevEl =
                                            prevRef.current
                                        swiperRef.current.params.navigation.nextEl =
                                            nextRef.current
                                        swiperRef.current.navigation.destroy()
                                        swiperRef.current.navigation.init()
                                        swiperRef.current.navigation.update()
                                    }
                                }, 0)
                            }}
                            onSlideChange={(swiper) => {
                                setActiveIndex(swiper.activeIndex)
                            }}
                        >
                            {preparedFiles.map((file, index) => (
                                <SwiperSlide
                                    key={index}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    {file.type === 'video' ? (
                                        <video
                                            src={file.src}
                                            controls
                                            style={{
                                                maxHeight: '80vh',
                                                maxWidth: '70%',
                                            }}
                                        />
                                    ) : file.type === 'image' ? (
                                        <div className="swiper-zoom-container">
                                            <img
                                                src={file.src}
                                                alt={`slide-${index}`}
                                                style={{
                                                    maxHeight: '80vh',
                                                    maxWidth: '70%',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        </div>
                                    ) : file.type === 'pdf' ? (
                                        <iframe
                                            src={file.src}
                                            style={{
                                                width: '70%',
                                                height: '80vh',
                                                border: 'none',
                                            }}
                                            title={`pdf-${index}`}
                                        />
                                    ) : file.type === 'pptx' ? (
                                        <iframe
                                            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.src)}`}
                                            style={{
                                                width: '70%',
                                                height: '80vh',
                                                border: 'none',
                                            }}
                                            title={`pptx-${index}`}
                                        />
                                    ) : (
                                        <div
                                            style={{
                                                color: 'white',
                                                fontSize: 18,
                                                textAlign: 'center',
                                            }}
                                        >
                                            File type not previewable
                                        </div>
                                    )}
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                </Modal>
            </div>
        </FilesScrollRootContext.Provider>
    )
}

export default ProjectFiles
